import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { TrendingUp, AlertTriangle, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const fmtMoney = (v) =>
  v == null ? "—" : `$${Math.round(Number(v)).toLocaleString()}`;
const fmtRate = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`);
const fmtDscr = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}x`);
const fmtPct = (v) =>
  v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`;

const dscrTone = (dscr) => {
  if (dscr == null) return "text-slate-500";
  if (dscr >= 1.25) return "text-emerald-700";
  if (dscr >= 1.0) return "text-amber-700";
  return "text-red-700";
};

const beTone = (be) => {
  if (be == null) return "text-slate-500";
  if (be < 0.7) return "text-emerald-700";
  if (be < 0.9) return "text-amber-700";
  return "text-red-700";
};

/**
 * Loan rate-shock stress test — shows current loan + 3 shock scenarios
 * (default +100/+200/+300 bps) side by side. Lenders ask for this analysis
 * during refi conversations; the math is powered by the same `calc_mortgage`
 * helper that drives every other underwriting number on the platform.
 */
export default function LoanStressTestDialog({ loan, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!loan) return;
    setLoading(true);
    try {
      const r = await api.post(`/loans/${loan.id}/stress-test`, {});
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Stress test failed");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && loan) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loan?.id]);

  if (!loan) return null;

  const current = data?.current;
  const shocks = data?.shocks || [];
  const noNoi = current && (current.annual_noi == null || current.annual_noi <= 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-600" />
            Rate-shock stress test
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4" data-testid="loan-stress-test">
          <div className="text-xs text-slate-500 leading-relaxed">
            How {loan.loan_number} performs under <strong>+100 bps</strong>, <strong>+200 bps</strong>,
            and <strong>+300 bps</strong> rate shocks against today's outstanding balance and
            remaining term. Use this in refi conversations to show downside protection or to size
            additional cushion.
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-400 text-sm">Computing…</div>
          ) : !data ? (
            <div className="text-center py-12 text-slate-400 text-sm">No data.</div>
          ) : (
            <>
              {noNoi && (
                <div className="border border-amber-200 bg-amber-50 text-amber-900 text-xs p-3 rounded-sm flex items-start gap-2" data-testid="stress-test-no-noi-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    No active leases or YTD expenses on this property — DSCR and break-even
                    occupancy will not display. Add a lease + expenses to see those metrics.
                  </span>
                </div>
              )}

              <div className="overflow-x-auto border border-slate-200 rounded-sm">
                <table className="w-full text-sm" data-testid="stress-test-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left">
                      <th className="px-4 py-2.5 overline text-slate-600">Metric</th>
                      <th className="px-4 py-2.5 overline text-slate-600 text-right">Current</th>
                      {shocks.map((s) => (
                        <th
                          key={s.shock_bps}
                          className="px-4 py-2.5 overline text-slate-600 text-right"
                          data-testid={`stress-shock-header-${s.shock_bps}`}
                        >
                          +{s.shock_bps} bps
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="font-mono text-xs">
                    <tr className="border-b border-slate-100">
                      <td className="px-4 py-3 text-slate-600">Rate</td>
                      <td className="px-4 py-3 text-right font-bold text-slate-900">{fmtRate(current.rate)}</td>
                      {shocks.map((s) => (
                        <td key={s.shock_bps} className="px-4 py-3 text-right text-slate-700">
                          {fmtRate(s.rate)}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="px-4 py-3 text-slate-600">Monthly P&I</td>
                      <td className="px-4 py-3 text-right font-bold text-slate-900">{fmtMoney(current.monthly_payment)}</td>
                      {shocks.map((s) => (
                        <td
                          key={s.shock_bps}
                          className="px-4 py-3 text-right text-slate-900 font-bold"
                          data-testid={`stress-monthly-${s.shock_bps}`}
                        >
                          {fmtMoney(s.monthly_payment)}
                          {s.monthly_payment_increase > 0 ? (
                            <div className="text-[10px] text-amber-700 font-normal mt-0.5">
                              +{fmtMoney(s.monthly_payment_increase)} / mo
                            </div>
                          ) : null}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="px-4 py-3 text-slate-600">Annual debt service</td>
                      <td className="px-4 py-3 text-right text-slate-700">{fmtMoney(current.annual_debt_service)}</td>
                      {shocks.map((s) => (
                        <td key={s.shock_bps} className="px-4 py-3 text-right text-slate-700">
                          {fmtMoney(s.annual_debt_service)}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="px-4 py-3 text-slate-600">DSCR</td>
                      <td className={`px-4 py-3 text-right font-bold ${dscrTone(current.dscr)}`}>{fmtDscr(current.dscr)}</td>
                      {shocks.map((s) => (
                        <td
                          key={s.shock_bps}
                          className={`px-4 py-3 text-right font-bold ${dscrTone(s.dscr)}`}
                          data-testid={`stress-dscr-${s.shock_bps}`}
                        >
                          {fmtDscr(s.dscr)}
                          {s.dscr_change != null && s.dscr_change !== 0 ? (
                            <div className="text-[10px] text-slate-500 font-normal mt-0.5">
                              {s.dscr_change > 0 ? "+" : ""}{s.dscr_change.toFixed(2)}x
                            </div>
                          ) : null}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-slate-600">Break-even occupancy</td>
                      <td className={`px-4 py-3 text-right font-bold ${beTone(current.breakeven_occupancy)}`}>
                        {fmtPct(current.breakeven_occupancy)}
                      </td>
                      {shocks.map((s) => (
                        <td
                          key={s.shock_bps}
                          className={`px-4 py-3 text-right font-bold ${beTone(s.breakeven_occupancy)}`}
                          data-testid={`stress-breakeven-${s.shock_bps}`}
                        >
                          {fmtPct(s.breakeven_occupancy)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                  <div className="overline text-slate-500">Outstanding balance</div>
                  <div className="font-mono font-bold text-slate-900 mt-1">{fmtMoney(current.outstanding_balance)}</div>
                </div>
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                  <div className="overline text-slate-500">Remaining term</div>
                  <div className="font-mono font-bold text-slate-900 mt-1">
                    {current.remaining_months} months
                  </div>
                </div>
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                  <div className="overline text-slate-500">Annual NOI</div>
                  <div className="font-mono font-bold text-slate-900 mt-1">{fmtMoney(current.annual_noi)}</div>
                </div>
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                  <div className="overline text-slate-500">Annual gross potential rent</div>
                  <div className="font-mono font-bold text-slate-900 mt-1">{fmtMoney(current.annual_gross_potential_rent)}</div>
                </div>
              </div>

              <div className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-3">
                <strong>Reading the table:</strong> DSCR ≥ 1.25x is the typical lender minimum for
                stabilized CRE. Break-even occupancy &gt; 90% means the asset can't carry its debt
                if a single major tenant moves out. Rows turn amber/red when those thresholds are
                crossed.
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={load}
                  className="btn-outline-dark flex items-center gap-2 text-xs"
                  data-testid="stress-test-refresh-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Recompute
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
